import { useState, useCallback, useEffect, useRef } from 'react'
import { GameStats, Alert, Tile, KeyState, WordLength } from '../types/game'
import { GAME_MODES, ALERT_TIME } from '../constants/gameConfig'
import { getDayOffset } from '../utils/dateUtils'
import { WordValidationService } from '../services/wordValidation'
import { LanguageService } from '../services/languageService'
import { challengeApi } from '../services/challengeApi'
import {
  getCurrentGame,
  setCurrentGame,
  clearCurrentGame,
  getGameStats,
  setGameStats,
  setLastPlayed,
  saveCompletedGameDetail,
  getGuestName,
  recordTimeSpent,
} from '../utils/storage'
import {
  trackGameStart,
  trackGameCompletion,
  trackGuessSubmitted,
  trackGameAbandoned,
  trackInvalidWordSubmission,
  trackLetterDistribution,
  trackTimePerGuess,
  trackRageQuitIndicators,
  trackSuccessfulWordPattern,
} from '../services/gameAnalytics'
import { markModeAsPlayed } from '../utils/storage'

// Map API pattern ('present', 'absent') to internal TileState ('wrong-location', 'wrong')
const mapPatternToTileState = (pattern: 'correct' | 'present' | 'absent'): KeyState => {
  if (pattern === 'present') return 'wrong-location'
  if (pattern === 'absent') return 'wrong'
  return 'correct'
}

interface UseWordleProps {
  mode: WordLength
}

interface UseWordleReturn {
  guesses: Tile[][]
  currentRow: number
  currentTile: number
  gameOver: boolean
  alerts: Alert[]
  keyStates: Record<string, KeyState>
  stats: GameStats
  addLetter: (letter: string) => void
  removeLetter: () => void
  submitGuess: () => void
  resetGame: () => void
  won: boolean
  secondsTaken: number | null
  targetWord: string
  definition: string
  isLoading: boolean
  error: string | null
  sessionId: string | null
  setterNote?: any
}

export const useWordle = ({ mode }: UseWordleProps): UseWordleReturn => {
  // Track game start time for analytics
  const gameStartTimeRef = useRef<number>(Date.now())

  // Track guess start time for time per guess analytics
  const guessStartTimeRef = useRef<number>(Date.now())

  // Track consecutive invalid submissions for rage quit detection
  const invalidSubmissionsRef = useRef<number>(0)

  // Track letter frequency for analytics
  const letterFrequencyRef = useRef<Record<string, number>>({})

  // Get language service and current game mode language
  const languageService = LanguageService.getInstance()
  const gameLanguage = GAME_MODES[mode].language

  // Initialize guesses - we'll use this in our state initialization
  const getInitialGuesses = () => {
    const savedGame = getCurrentGame()
    if (savedGame && savedGame.mode === mode) {
      return savedGame.guesses
    }

    // Create empty grid based on selected mode
    return Array(GAME_MODES[mode].maxGuesses)
      .fill(null)
      .map(() =>
        Array(mode) // Use mode for column count
          .fill(null)
          .map(() => ({ letter: '', state: 'empty' as const }))
      )
  }

  // Reset game when mode changes
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      guesses: Array(GAME_MODES[mode].maxGuesses)
        .fill(null)
        .map(() =>
          Array(mode)
            .fill(null)
            .map(() => ({ letter: '', state: 'empty' as const }))
        ),
      currentRow: 0,
      currentTile: 0,
      gameOver: false,
      keyStates: {},
    }))

    // Reset analytics tracking references
    guessStartTimeRef.current = Date.now()
    invalidSubmissionsRef.current = 0
    letterFrequencyRef.current = {}

    // Reset other game state as needed
  }, [mode])

  const validationService = WordValidationService.getInstance()

  const dayOffset = getDayOffset()

  // Initialize state from storage or defaults
  const [state, setState] = useState(() => {
    const savedGame = getCurrentGame()
    if (savedGame && savedGame.dayOffset === dayOffset) {
      return {
        guesses: savedGame.guesses,
        currentRow: savedGame.currentRow,
        currentTile: savedGame.currentTile,
        gameOver: savedGame.gameOver,
        keyStates: savedGame.keyStates,
      }
    }

    // Track new game start for analytics
    try {
      trackGameStart(mode)

      // Initialize tracking for new game
      guessStartTimeRef.current = Date.now()
      invalidSubmissionsRef.current = 0
      letterFrequencyRef.current = {}
    } catch (error) {
      console.error('Failed to track game start:', error)
    }

    return {
      guesses: getInitialGuesses(),
      currentRow: 0,
      currentTile: 0,
      gameOver: false,
      keyStates: {},
    }
  })

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [revealedTarget, setRevealedTarget] = useState<string>('')
  const [revealedDefinition, setRevealedDefinition] = useState<string>('')

  const [alerts, setAlerts] = useState<Alert[]>([])
  const [stats, setStats] = useState(() => getGameStats())
  const [secondsTaken, setSecondsTaken] = useState<number | null>(null)
  const [setterNote, setSetterNote] = useState<any>(null)

  // Destructure state for convenience
  const { guesses, currentRow, currentTile, gameOver, keyStates } = state

  // Current target word - only revealed by server on finish
  const activeTargetWord = revealedTarget

  const addAlert = useCallback((message: string, duration = 1000) => {
    const id = Math.random().toString()
    setAlerts((prev) => [...prev, { message, duration, id }])
    if (duration) {
      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== id))
      }, duration)
    }
  }, [])

  // Initialize server session on mount or mode change
  useEffect(() => {
    const initChallenge = async () => {
      setIsLoading(true)
      setServerError(null)
      try {
        const config = await challengeApi.startChallenge({
          mode: 'daily', // or from props if available
          length: mode,
          language: gameLanguage.code,
          guestName: getGuestName() ?? undefined,
        })
        setSessionId(config.sessionId)

        // If the server already has guesses (e.g. resuming after local storage
        // was cleared, or on a different device), rebuild the grid from them
        // instead of leaving the freshly-initialized empty grid in place.
        if (config.guessCount > 0) {
          const restoredRows: Tile[][] = config.guesses.map((g) =>
            (g.guess as string)
              .split('')
              .map((letter: string, i: number) => ({
                letter,
                state: mapPatternToTileState(g.pattern[i]),
              }))
          )

          const emptyRow = (): Tile[] =>
            Array(mode)
              .fill(null)
              .map(() => ({ letter: '', state: 'empty' as const }))

          const paddedRows = [
            ...restoredRows,
            ...Array(Math.max(GAME_MODES[mode].maxGuesses - restoredRows.length, 0))
              .fill(null)
              .map(emptyRow),
          ]

          const restoredKeyStates = config.guesses.reduce<Record<string, KeyState>>(
            (acc, g) => {
              ;(g.guess as string).split('').forEach((letter: string, i: number) => {
                acc[letter.toUpperCase()] = mapPatternToTileState(g.pattern[i])
              })
              return acc
            },
            {}
          )

          setState((prev) => ({
            ...prev,
            guesses: paddedRows,
            currentRow: restoredRows.length,
            currentTile: 0,
            keyStates: { ...prev.keyStates, ...restoredKeyStates },
          }))
        }

        if (config.status !== 'active') {
          setState(prev => ({ ...prev, gameOver: true }))
          if (config.setterNote) {
            setSetterNote(config.setterNote)
          }
          if (config.targetWord) {
            setRevealedTarget(config.targetWord)
          }
          if (config.definition) {
            setRevealedDefinition(config.definition)
          }
          // Backfills a session this client never observed completing live
          // (new device, cleared local storage) - no-ops if this mode was
          // already recorded today, e.g. by the live win/loss path below.
          if (typeof config.totalTimeSeconds === 'number') {
            recordTimeSpent(config.totalTimeSeconds, `wordle-${mode}`)
          }
        }
      } catch (err: any) {
        setServerError(err.message || 'Connection required to play')
        addAlert('Connection required to play.', ALERT_TIME.ERROR)
        // Set gameOver to true to prevent starting the game without session
        setState(prev => ({ ...prev, gameOver: true }))
      } finally {
        setIsLoading(false)
      }
    }

    initChallenge()
  }, [mode, gameLanguage, addAlert])

  // Save game progress whenever state changes
  useEffect(() => {
    if (gameOver) {
      clearCurrentGame()
      setLastPlayed(Date.now())


      // Mark this mode as played for today
      markModeAsPlayed(mode)
    } else {
      setCurrentGame({
        ...state,
        dayOffset,
        mode,
      })
    }
  }, [state, dayOffset, gameOver, mode])

  // Update statistics when game ends
  const updateStats = useCallback(
    (won: boolean) => {
      setStats((prev) => {
        const newStats: GameStats = {
          gamesPlayed: prev.gamesPlayed + 1,
          gamesWon: prev.gamesWon + (won ? 1 : 0),
          currentStreak: won ? prev.currentStreak + 1 : 0,
          maxStreak: won
            ? Math.max(prev.currentStreak + 1, prev.maxStreak)
            : prev.maxStreak,
          guessDistribution: {
            ...prev.guessDistribution,
            [currentRow + 1]:
              (prev.guessDistribution[currentRow + 1] || 0) + (won ? 1 : 0),
          },
        }
        setGameStats(newStats)

        // Track game completion for analytics
        const timeTaken = Date.now() - gameStartTimeRef.current
        const secondsTakenValue = Math.round(timeTaken / 1000)
        setSecondsTaken(secondsTakenValue)
        recordTimeSpent(secondsTakenValue, `wordle-${mode}`)

        try {
          trackGameCompletion(mode, won, currentRow + 1, timeTaken)

          // Track successful word pattern if won
          if (won) {
            // Create a pattern representation of the word
            // (e.g., COCOC for consonant-vowel pattern)
            const pattern = activeTargetWord
              .split('')
              .map((char) => {
                const isVowel = /[aeiou]/i.test(char)
                return isVowel ? 'V' : 'C'
              })
              .join('')

            trackSuccessfulWordPattern(mode, pattern, currentRow + 1)
          }
        } catch (error) {
          console.error('Failed to track game completion:', error)
        }

        return newStats
      })
    },
    [currentRow, mode, activeTargetWord]
  )


  const addLetter = useCallback(
    (letter: string) => {
      if (gameOver || currentTile >= mode) return

      // Start timing this guess if it's the first letter
      if (currentTile === 0) {
        guessStartTimeRef.current = Date.now()
      }

      // Check if the letter is valid for the current language
      if (!languageService.isValidCharacter(letter, gameLanguage)) {
        // Show an error message
        addAlert(
          languageService.getInvalidCharacterMessage(letter, gameLanguage),
          ALERT_TIME.ERROR
        )
        return
      }

      setState((prev) => {
        // Ensure we're not adding letters beyond the allowed limit
        if (prev.currentTile >= mode) return prev

        const newGuesses = [...prev.guesses]
        const newRow = [...newGuesses[currentRow]]
        // For apostrophes, keep them as-is; otherwise lowercase the letter
        const letterToAdd = letter === "'" ? letter : letter.toLowerCase()
        newRow[currentTile] = {
          letter: letterToAdd,
          state: 'active',
        }
        newGuesses[currentRow] = newRow
        return {
          ...prev,
          guesses: newGuesses,
          currentTile: Math.min(prev.currentTile + 1, mode),
        }
      })

      // Track letter frequency for analytics
      try {
        const normalizedLetter = letter.toLowerCase()
        letterFrequencyRef.current[normalizedLetter] =
          (letterFrequencyRef.current[normalizedLetter] || 0) + 1
      } catch (error) {
        console.error('Failed to track letter frequency:', error)
      }
    },
    [
      currentRow,
      currentTile,
      gameOver,
      mode,
      languageService,
      gameLanguage,
      addAlert,
    ]
  )

  const removeLetter = useCallback(() => {
    if (gameOver || currentTile === 0) return

    const newTile = currentTile - 1
    setState((prev) => {
      const newGuesses = [...prev.guesses]
      const newRow = [...newGuesses[currentRow]]
      newRow[newTile] = {
        letter: '',
        state: 'empty',
      }
      newGuesses[currentRow] = newRow
      return {
        ...prev,
        guesses: newGuesses,
        currentTile: newTile,
      }
    })
  }, [currentRow, currentTile, gameOver])

  // Create a ref for the reset function
  const resetGameRef = useRef<(() => void) | null>(null)

  // Define the reset function
  const resetGame = useCallback(() => {
    // Track game abandoned if resetting mid-game
    if (!gameOver && currentRow > 0) {
      try {
        trackGameAbandoned(mode, currentRow)

        // Check for rage quit indicators
        if (invalidSubmissionsRef.current >= 3) {
          trackRageQuitIndicators(
            mode,
            invalidSubmissionsRef.current,
            true,
            currentRow
          )
        }
      } catch (error) {
        console.error('Failed to track game abandon:', error)
      }
    }

    // Reset game start time for analytics
    gameStartTimeRef.current = Date.now()

    // Reset analytics tracking references
    guessStartTimeRef.current = Date.now()
    invalidSubmissionsRef.current = 0
    letterFrequencyRef.current = {}

    // Clear current game from storage
    clearCurrentGame()

    // Reset the state completely
    setState({
      guesses: Array(GAME_MODES[mode].maxGuesses)
        .fill(null)
        .map(() =>
          Array(GAME_MODES[mode].wordLength)
            .fill(null)
            .map(() => ({ letter: '', state: 'empty' as const }))
        ),
      currentRow: 0,
      currentTile: 0,
      gameOver: false,
      keyStates: {},
    })

    // Clear alerts
    setAlerts([])

    // Track new game start for analytics
    try {
      trackGameStart(mode)
    } catch (error) {
      console.error('Failed to track game start:', error)
    }
  }, [mode, addAlert, gameOver, currentRow])

  // Store the latest resetGame function in the ref
  useEffect(() => {
    resetGameRef.current = resetGame
  }, [resetGame])

  const submitGuess = useCallback(async () => {
    if (gameOver || isLoading || !sessionId) return

    setIsLoading(true)

    try {
      // Calculate time spent on this guess
      const timeSpentMs = Date.now() - guessStartTimeRef.current
      const timeSpentSeconds = Math.floor(timeSpentMs / 1000)

      // Use the same calculation here
      const currentGuessValue = guesses[currentRow]
        .map((tile) => tile.letter)
        .join('')

      // Validate the guess
      const validationResult = validationService.validateWord(
        currentGuessValue,
        GAME_MODES[mode].wordLength
      )

      if (!validationResult.isValid) {
        // Track invalid submission for rage quit detection
        invalidSubmissionsRef.current += 1

        // Declare variables outside the switch
        const currentLength = currentGuessValue.length
        const expectedLength = GAME_MODES[mode].wordLength

        // Track invalid word submission
        try {
          trackInvalidWordSubmission(mode, currentGuessValue, currentRow + 1)
        } catch (error) {
          console.error('Failed to track invalid word submission:', error)
        }

        switch (validationResult.error?.code) {
          case 'INVALID_LENGTH':
            // More precise error message based on word length
            if (currentLength < expectedLength) {
              addAlert('Not enough letters', ALERT_TIME.ERROR)
            } else if (currentLength > expectedLength) {
              addAlert('Too many letters', ALERT_TIME.ERROR)
            } else {
              // This shouldn't happen, but just in case
              addAlert('Invalid word length', ALERT_TIME.ERROR)
            }
            return
          case 'NOT_IN_DICTIONARY':
            addAlert('Not in word list', ALERT_TIME.ERROR)
            return
          case 'INVALID_CHARS':
            addAlert('Invalid characters', ALERT_TIME.ERROR)
            return
          default:
            addAlert('Invalid word', ALERT_TIME.ERROR)
            return
        }
      }

      // TODO: Uncomment this when we have a ChiShona dictionary alright
      // if (!isValidWord(currentGuess, dictionary)) {
      //   addAlert('Not in word list')
      //   return
      // }

      // Reset consecutive invalid submissions counter on valid submission
      invalidSubmissionsRef.current = 0

      // Reset guess start time for next guess
      guessStartTimeRef.current = Date.now()

      const result = await challengeApi.submitGuess(sessionId, currentGuessValue)
      if (result.setterNote) {
        setSetterNote(result.setterNote)
      }
      if (result.definition) {
        setRevealedDefinition(result.definition)
      }
      const results = result.pattern.map(mapPatternToTileState)

      // Track letter distributions for each letter in the guess
      try {
        currentGuessValue.split('').forEach((letter, index) => {
          const isCorrect = results[index] === 'correct'
          trackLetterDistribution(
            mode,
            letter,
            letterFrequencyRef.current[letter] || 1,
            isCorrect
          )
        })

        // Track time per guess
        const isCorrect = result.status === 'won'
        trackTimePerGuess(mode, currentRow + 1, timeSpentSeconds, isCorrect)
      } catch (error) {
        console.error('Failed to track letter analytics:', error)
      }

      if (result.targetWord) {
        setRevealedTarget(result.targetWord)
      }

      setState((prev) => ({
        ...prev,
        guesses: prev.guesses.map((row, i) =>
          i === currentRow
            ? row.map((tile, j) => ({
                ...tile,
                state: results[j],
              }))
            : row
        ),
        keyStates: {
          ...prev.keyStates,
          ...currentGuessValue.split('').reduce(
            (acc, letter, i) => ({
              ...acc,
              [letter.toUpperCase()]: results[i],
            }),
            {}
          ),
        },
      }))

      // Track guess submission for analytics
      try {
        const isCorrect = result.status === 'won'
        trackGuessSubmitted(mode, currentRow + 1, isCorrect)
      } catch (error) {
        console.error('Failed to track guess submission:', error)
      }

      if (result.status === 'won') {
        const finalGuesses = guesses.map((row, i) =>
          i === currentRow
            ? row.map((tile, j) => ({
                letter: tile.letter,
                state: results[j],
              }))
            : row
        )
        saveCompletedGameDetail(mode, finalGuesses, true, activeTargetWord)

        setState((prev) => ({ ...prev, gameOver: true }))
        updateStats(true)
        return
      }

      if (result.status === 'lost') {
        const finalGuesses = guesses.map((row, i) =>
          i === currentRow
            ? row.map((tile, j) => ({
                letter: tile.letter,
                state: results[j],
              }))
            : row
        )
        saveCompletedGameDetail(mode, finalGuesses, false, activeTargetWord)

        setState((prev) => ({ ...prev, gameOver: true }))
        updateStats(false)
        const displayWord = result.targetWord || ''
        addAlert(displayWord.toUpperCase(), ALERT_TIME.PERMANENT)
        return
      }

      setState((prev) => ({
        ...prev,
        currentRow: prev.currentRow + 1,
        currentTile: 0,
      }))
    } catch (err: any) {
      setServerError(err.message)
      addAlert(err.message || 'Error submitting guess', ALERT_TIME.ERROR)
    } finally {
      setIsLoading(false)
    }
  }, [
    gameOver,
    isLoading,
    sessionId,
    currentRow,
    guesses,
    mode,
    updateStats,
    addAlert,
    validationService,
    dayOffset,
  ])

  return {
    guesses,
    currentRow,
    currentTile,
    gameOver,
    alerts,
    keyStates,
    stats,
    addLetter,
    removeLetter,
    submitGuess,
    resetGame,
    won: state.guesses.some(row => row.length > 0 && row.every(tile => tile.state === 'correct')),
    secondsTaken,
    targetWord: activeTargetWord,
    definition: revealedDefinition,
    isLoading,
    error: serverError,
    sessionId,
    setterNote,
  }
}
